/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observe } from "mobx";
import { apiNames } from "constants/apiNames";
import * as stores from "stores";
import { publicApiNames, excludedMobileMVApis } from "../publicApis/constants";
import { ApiWrapper } from "../api/external/ApiWrapper";
import { getTotalPages, getAssetURI, shouldLoadAcroformData } from "selectors/documentSelectors";
import {
  getPageOverlays,
  getShouldRenderPersonalCommentsDropin,
  getShouldRenderCommentsDropin,
  getShouldRenderDropin,
  isDropinLoaded,
  isDropinLoading,
  getShouldRenderEditDropin,
  getIsDemoMode,
  getShowEditOnTextSelection,
  getShouldRenderCropPagesDropin,
  getShouldRenderNumberPagesDropin,
  getShouldRenderFillSignDropin,
  getShouldRenderPdfRequestSignaturesDropin,
  getShouldRenderSignAuthoring2Dropin,
  getShouldRenderSignAuthoringDropin,
  getShouldRenderVisualizerDropin,
  getShouldRenderESignOverlayDropin,
  getOverlayDropinLoadedPromise,
  getShouldEnableVoiceAnnotations,
  getEditDropinMountedPromise,
  getShouldDeferLoadOverlays,
  getShouldPreloadEditModule
} from "selectors/overlayDropinSelectors";
import {
  getShouldUseModernViewer,
  getFontAccessType,
  getEnableEditForFreeUsers,
  getUseAFFontPicker,
  getEditEnterInfo,
  getShouldUseMobileModernViewer,
  getShouldEnableEditGenAI,
  getFillSignConfig,
  getDisableEditFeatures,
  getShowPremiumIndicatorForEditMegaVerb,
  getCommentsAdditionalConfig
} from "selectors/appSelectors";
import { isPersonalCommentingEnabled } from "utils/FeatureFlagUtils";
import { providers } from "dc-core";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import { isInitialPageLoaded } from "selectors/pagedViewState";
import * as constants from "constants/constants";

class OverlaysManager {
  constructor(apiFactory, addOverlayDropinAction) {
    this.apiFactory = apiFactory;
    this.registeredPlugins = {};
    this._overlayApiProvider = this._overlayApiProvider.bind(this);
    this._registerPageOverlay = this._registerPageOverlay.bind(this);
    this._loadOverlayDropin = this._loadOverlayDropin.bind(this);
    this.addOverlayDropinAction = addOverlayDropinAction;
    this._registerApis();

    this.shouldLoadCommentsDropin = false;
    this.canLoadCommentsDropin = false;
    this._highlightFlagChange;
  }

  get pageOverlays() {
    return getPageOverlays(stores);
  }

  _registerApis() {
    this.apiFactory.registerApi(apiNames.REGISTER_PAGE_OVERLAY, this._overlayApiProvider);
  }

  _overlayApiProvider(clientName) {
    return ApiWrapper(clientName, this._registerPageOverlay);
  }

  _registerPageOverlay(clientName, component, api = {}) {
    if (component) {
      stores.overlayDropinStore.addPageOverlay({ clientName, component, api });
      return true;
    }
    return false;
  }

  getClientName(dropinName) {
    let clientName = dropinName.toUpperCase();
    if (dropinName === "comments") clientName = "COMMENT";
    return clientName;
  }

  _loadOverlayDropin(dropinName, customOldApis = [], customPublicApis = [], config) {
    let shouldRenderDropin = getShouldRenderDropin(dropinName, stores);
    if (shouldRenderDropin && !isDropinLoaded(dropinName, stores) && !isDropinLoading(dropinName, stores)) {
      stores.overlayDropinStore.setOverlayDropinLoading(dropinName, true);
      let promise;
      if (dropinName === constants.ACROFORM_DROPIN_NAME) {
        promise = import(/* webpackChunkName: "acroform-chunk" */ "acroformDropin/acroformDropin").then(
          AcroformDropin => AcroformDropin.default
        );
      } else {
        promise = providers.discovery().then(d => d.loadDropinClass(dropinName));
      }
      stores.overlayDropinStore.setOverlayDropinLoadedPromise(dropinName, promise);

      return promise
        .then(OverlayManager => {
          let name = this.getClientName(dropinName);
          let commonApis = [
            apiNames.REGISTER_PAGE_OVERLAY,
            apiNames.DOC_ACTIONS,
            apiNames.DOC_STATE,
            apiNames.DOC_EVENTS,
            apiNames.PAGE_VIEW,
            apiNames.MODIFY_CURSOR,
            apiNames.HUD,
            apiNames.MODIFY_BACKGROUND_COLOR,
            apiNames.CONTEXT_MENU,
            apiNames.REUSABLE_COMPONENTS,
            apiNames.COMMAND_MANAGER
          ];

          if (getShouldUseMobileModernViewer(stores)) {
            commonApis = commonApis.filter(api => !excludedMobileMVApis.includes(api));
          }

          commonApis = commonApis.concat(customOldApis);
          let publicApis = [
            publicApiNames.APP,
            publicApiNames.DOCUMENT,
            publicApiNames.RHP,
            publicApiNames.QUICKTOOLS,
            publicApiNames.BOTTOM_BOARD
          ];
          publicApis = publicApis.concat(customPublicApis);

          if (name && !this._alreadyExists(name)) {
            return this._getApiObject(name, commonApis).then(apiObject =>
              this._getApiObject(name, publicApis, true).then(publicApiObject => {
                let combinedApiObject = { ...apiObject, previewPublicApis: publicApiObject };
                this.registeredPlugins[name] = {
                  dropin: OverlayManager,
                  api: combinedApiObject
                };
                this.addOverlayDropinAction(dropinName, OverlayManager, combinedApiObject, config);
                stores.overlayDropinStore.setOverlayDropinLoaded(dropinName, true);
                stores.overlayDropinStore.setOverlayDropinLoading(dropinName, false);
              })
            );
          }
        })
        .catch(() => {
          stores.overlayDropinStore.setOverlayDropinLoading(dropinName, false);
          stores.overlayDropinStore.resetOverlayDropinLoadedPromise(dropinName);
        });
    }
    return getOverlayDropinLoadedPromise(stores, dropinName);

    // Dropin was already loaded. Not needed to load again.
  }

  loadCommentsDropinIfPossible(stores, commentCustomApis) {
    if (this.shouldLoadCommentsDropin && this.canLoadCommentsDropin) {
      this._loadOverlayDropin("comments", commentCustomApis, [publicApiNames.APP], {
        useModernViewer: getShouldUseModernViewer(stores),
        useMobileModernViewer: getShouldUseMobileModernViewer(stores),
        enableVoiceAnnotations: getShouldEnableVoiceAnnotations(stores),
        commentsAdditionalConfig: getCommentsAdditionalConfig(stores)
      });
    }
  }

  loadAcroformDropin() {
    const acroformApis = [apiNames.ACROFORM];
    return this._loadOverlayDropin("acroform", acroformApis, [publicApiNames.APP], {
      useModernViewer: getShouldUseModernViewer(stores)
    });
  }

  _loadEditOverlays() {
    subscribeToSelector(stores, getShouldRenderEditDropin, editDropinToRender => {
      let shouldRenderEdit = false;
      const editDropinMountedPromise = getEditDropinMountedPromise(stores);
      if (editDropinToRender === constants.EDIT_DROPIN_TYPE.PARTIAL) {
        editDropinMountedPromise.reject("Partial edit dropin is loaded");
        shouldRenderEdit = true;
        this._loadOverlayDropin("partial-edit", [apiNames.DOCUMENT], [publicApiNames.APP], {
          ajsAssetUri: getAssetURI(stores),
          usePremiumIndicator: getShowPremiumIndicatorForEditMegaVerb(stores),
          useModernViewer: getShouldUseModernViewer(stores),
          disableEditFeatures: getDisableEditFeatures(stores),
          showEditOnTextSelection: getShowEditOnTextSelection(stores),
          shouldPreloadEditModule: getShouldPreloadEditModule(stores),
          showDelayedUpsell: true
        });
      } else if (editDropinToRender === constants.EDIT_DROPIN_TYPE.FULL) {
        stores.overlayDropinStore.removeOverlayDropin("partial-edit");
        shouldRenderEdit = true;
        this._loadOverlayDropin(
          "edit",
          [apiNames.TOPBAR, apiNames.DOCUMENT],
          [publicApiNames.APP, publicApiNames.FLOATING_WINDOWS],
          {
            ajsAssetUri: getAssetURI(stores),
            usePremiumIndicator: getShowPremiumIndicatorForEditMegaVerb(stores),
            useModernViewer: getShouldUseModernViewer(stores),
            useAFFontPicker: getUseAFFontPicker(stores),
            fontAccessType: getFontAccessType(stores),
            editEnterInfo: getEditEnterInfo(stores),
            enableEditForFreeUsers: getEnableEditForFreeUsers(stores),
            shouldPreloadEditModule: getShouldPreloadEditModule(stores),
            isDemoMode: getIsDemoMode(stores),
            shouldEnableEditGenAI: getShouldEnableEditGenAI(stores)
          }
        );
      } else {
        editDropinMountedPromise.reject("Edit dropin is not loaded");
      }
      if (shouldRenderEdit) {
        providers["edit-provider"]().then(editProvider => {
          this._getApiObject("edit-provider", [apiNames.DOCUMENT]).then(apiObject => {
            editProvider.registerPreviewApis(apiObject);
          });
        });
      }
    });
    subscribeToSelector(stores, getShouldRenderCropPagesDropin, () => {
      this._loadOverlayDropin("crop", [apiNames.DOCUMENT], [publicApiNames.APP], {
        ajsAssetUri: getAssetURI(stores),
        useModernViewer: getShouldUseModernViewer(stores),
        isDemoMode: getIsDemoMode(stores),
        shouldPreloadEditModule: getShouldPreloadEditModule(stores)
      });
    });
    subscribeToSelector(stores, getShouldRenderNumberPagesDropin, () => {
      this._loadOverlayDropin("number-pages", [apiNames.DOCUMENT], [publicApiNames.APP], {
        ajsAssetUri: getAssetURI(stores),
        isDemoMode: getIsDemoMode(stores),
        shouldPreloadEditModule: getShouldPreloadEditModule(stores)
      });
    });
  }

  _loadFSOverlays() {
    subscribeToSelector(stores, getShouldRenderSignAuthoringDropin, () => {
      this._loadOverlayDropin("sign-authoring-overlay", [apiNames.DISABLE_CONTEXT_MENU]);
    });
    subscribeToSelector(stores, getShouldRenderSignAuthoring2Dropin, () => {
      this._loadOverlayDropin("sign-authoring2-overlay", [apiNames.DISABLE_CONTEXT_MENU]);
    });
    subscribeToSelector(stores, getShouldRenderESignOverlayDropin, () => {
      this._loadOverlayDropin("esign-overlay", [apiNames.DISABLE_CONTEXT_MENU]);
    });
    subscribeToSelector(stores, getShouldRenderFillSignDropin, () => {
      this._loadOverlayDropin(
        "fillsignoverlay",
        [apiNames.TOPBAR, apiNames.DOCUMENT, apiNames.PREVIEW, apiNames.ACROFORM],
        [publicApiNames.APP, publicApiNames.FILLANDSIGN, publicApiNames.QUICKTOOLS],
        {
          fillSignConfig: getFillSignConfig(stores)
        }
      );
    });
    subscribeToSelector(stores, getShouldRenderPdfRequestSignaturesDropin, () => {
      this._loadOverlayDropin(
        "pdf-requestsignatures-overlay",
        [apiNames.TOPBAR, apiNames.DOCUMENT, apiNames.PREVIEW, apiNames.ACROFORM, apiNames.COMMENTS],
        [publicApiNames.APP, publicApiNames.FILLANDSIGN, publicApiNames.QUICKTOOLS]
      );
    });
  }

  _loadCommentOverlays() {
    let commentCustomApis = [
      apiNames.COMMENTS,
      apiNames.PERSONAL_COMMENTING,
      apiNames.ENABLE_FREETEXT,
      apiNames.DISABLE_CONTEXT_MENU,
      apiNames.IS_INTEGRATION_CONTEXT
    ];

    if (isPersonalCommentingEnabled(stores)) {
      commentCustomApis.push(apiNames.TOPBAR);
    }

    if (getShouldUseModernViewer(stores)) {
      subscribeToSelector(stores, getShouldRenderPersonalCommentsDropin, () => {
        this.shouldLoadCommentsDropin = true;
        this.loadCommentsDropinIfPossible(stores, commentCustomApis);
      });
      subscribeToSelector(stores, getShouldRenderCommentsDropin, () => {
        this.shouldLoadCommentsDropin = true;
        this.loadCommentsDropinIfPossible(stores, commentCustomApis);
      });
      subscribeToSelector(stores, getTotalPages, () => {
        this.canLoadCommentsDropin = getTotalPages(stores) > 0;
        this.loadCommentsDropinIfPossible(stores, commentCustomApis);
      });
    } else {
      subscribeToSelector(stores, getShouldRenderPersonalCommentsDropin, () => {
        this._loadOverlayDropin("comments", commentCustomApis, [publicApiNames.APP], {
          useModernViewer: getShouldUseModernViewer(stores)
        });
      });
      subscribeToSelector(stores, getShouldRenderCommentsDropin, () => {
        this._loadOverlayDropin("comments", commentCustomApis, [publicApiNames.APP], {
          useModernViewer: getShouldUseModernViewer(stores)
        });
      });
    }
  }

  _loadTroveOverlays() {
    this._loadOverlayDropin("trove", [apiNames.TOPBAR, apiNames.DOCUMENT]);
  }

  _loadVisualizerOverlays() {
    subscribeToSelector(stores, getShouldRenderVisualizerDropin, () => {
      this._loadOverlayDropin("visualizer", [apiNames.TOPBAR, apiNames.DOCUMENT, apiNames.DOC_EVENTS]);
    });
  }

  _loadDciHighlightsOverlays() {
    this._highlightFlagChange = observe(stores.overlayDropinStore, "shouldRenderHighlightsDropin", change => {
      this._loadOverlayDropin("dci-highlights");
    });
    this._loadOverlayDropin("dci-highlights", [apiNames.MODE_CHANGE]);
  }

  _loadAllOverlays() {
    this._loadFSOverlays();
    this._loadEditOverlays();
    this._loadCommentOverlays();
    this._loadTroveOverlays();
    this._loadDciHighlightsOverlays();
    this._loadVisualizerOverlays();
  }

  _deferLoadOverlays() {
    const shouldDeferLoadOverlays = getShouldDeferLoadOverlays(stores);
    if (!shouldDeferLoadOverlays) {
      this._loadAllOverlays();
    } else {
      subscribeToSelector(stores, isInitialPageLoaded, val => {
        if (val) {
          this._loadAllOverlays();
        }
      });
    }
  }

  loadOverlayDropins() {
    const acroformApis = [apiNames.ACROFORM];
    subscribeToSelector(stores, shouldLoadAcroformData, shouldRender => {
      if (shouldRender) this.loadAcroformDropin();
    });
    this._deferLoadOverlays();
  }

  _alreadyExists(name) {
    return this.registeredPlugins[name] !== undefined;
  }

  _getApiObject(clientName, requestedApiList, isNew = false) {
    return this.apiFactory.getExposedApis(clientName, requestedApiList, true, isNew);
  }
}

export { OverlaysManager };
